/**
 * Conflict zone layer renderer.
 * GeoJsonLayer rendering the static CONFLICT_ZONES dataset as red polygon
 * washes (WM DeckGLMap.ts:2895-2914) — the only polygon sign in the
 * conflict group, visually unmistakable against every point layer.
 * Whole-country conflicts substitute the real country multipolygon
 * (WM DeckGLMap.ts:366-371, 2740-2782) so they render as exact outlines.
 */

import { GeoJsonLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { Feature, FeatureCollection } from 'geojson';
import { CONFLICT_ZONE_COLORS } from '@/map/layer-colors';
import { CONFLICT_ZONES } from '@/config/data/geo';
import { getCountryGeoJson, isCountryDetectionReady } from '@/map/country-detect';

/** Layer IDs managed by this renderer. */
export const CONFLICT_ZONE_LAYER_IDS = new Set(['conflict-zones']);

/** Zones rendered with real country geometry instead of hand-drawn coords. */
const CONFLICT_COUNTRY_ISO: Record<string, string[]> = {
  iran: ['IR'],
  ukraine: ['UA'],
  sudan: ['SD'],
  myanmar: ['MM'],
};

/** Uniform red wash — matching WM (all zones render the same on the flat map). */
const FILL_COLOR: [number, number, number, number] = [...CONFLICT_ZONE_COLORS.high.fill] as [number, number, number, number];
const STROKE_COLOR: [number, number, number, number] = [...CONFLICT_ZONE_COLORS.high.stroke] as [number, number, number, number];

/** Close a polygon ring if the first and last coordinates differ. */
export function ensureClosedRing(coords: [number, number][]): [number, number][] {
  if (coords.length === 0) return coords;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coords;
  return [...coords, first];
}

let cachedCollection: FeatureCollection | null = null;
let cachedWithCountries = false;

/**
 * Build the zone FeatureCollection. Cached until country geometry finishes
 * loading (async at boot), at which point the ISO-mapped zones upgrade from
 * hand-drawn polygons to real country multipolygons.
 */
export function buildConflictZoneFeatures(): FeatureCollection {
  const countriesReady = isCountryDetectionReady();
  if (cachedCollection && cachedWithCountries === countriesReady) {
    return cachedCollection;
  }

  const features: Feature[] = [];

  for (const zone of CONFLICT_ZONES) {
    const properties = { id: zone.id, name: zone.name, intensity: zone.intensity ?? 'high' };
    const isoCodes = CONFLICT_COUNTRY_ISO[zone.id];
    let usedCountryGeometry = false;

    if (isoCodes && countriesReady) {
      for (const code of isoCodes) {
        const country = getCountryGeoJson(code);
        if (!country) continue;
        features.push({
          type: 'Feature',
          properties,
          geometry: country.geometry as unknown as Feature['geometry'],
        });
        usedCountryGeometry = true;
      }
    }

    if (usedCountryGeometry) continue;

    features.push({
      type: 'Feature',
      properties,
      geometry: { type: 'Polygon', coordinates: [ensureClosedRing(zone.coords)] },
    });
  }

  cachedCollection = { type: 'FeatureCollection', features };
  cachedWithCountries = countriesReady;
  return cachedCollection;
}

/** Create deck.gl layers for the conflict zone polygons. */
export function createConflictZoneLayers(): Layer[] {
  const data = buildConflictZoneFeatures();
  if (data.features.length === 0) return [];

  return [
    new GeoJsonLayer<Feature>({
      id: 'conflict-zones',
      data,
      pickable: true,
      filled: true,
      stroked: true,
      lineWidthUnits: 'pixels' as const,
      getFillColor: FILL_COLOR,
      getLineColor: STROKE_COLOR,
      getLineWidth: 2,
      lineWidthMinPixels: 1,
      // No opacity ramp — WM carries the whole effect in the fill alpha (100).
    }),
  ];
}
